import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { 
  Upload, 
  File, 
  X, 
  Play, 
  Download, 
  RefreshCw,
  Languages,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Eye,
  Calendar,
  Timer,
  Hash,
  Users
} from 'lucide-react'
import api from '../utils/api'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import QuotaStatus from '../components/QuotaStatus'
import MultiFileDownload from '../components/ui/MultiFileDownload'
import JobDownloadButton from '../components/ui/JobDownloadButton'
import ImportJobModal from '../components/tms/ImportJobModal'
import { useJobResults, useArchiveGeneration } from '../hooks/useJobResults'
import { SUPPORTED_LANGUAGES, SUPPORTED_DOMAINS, DEFAULT_DOMAIN, DEFAULT_CONTEXT_KEY, MAX_CONTEXT_LENGTH, SUPPORTED_FILE_TYPES, MAX_FILE_SIZE, JOB_STATUS_LABELS, JOB_STATUS_COLORS } from '../utils/constants'

const TranslationPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Helper function to calculate job duration
  const calculateDuration = (job) => {
    if (job.duration_display) {
      return job.duration_display
    }
    
    // For completed/finished jobs, calculate duration from timestamps
    if ((job.status === 'completed' || job.status === 'finished') && job.created_at) {
      const startTime = new Date(job.created_at)
      const endTime = job.updated_at ? new Date(job.updated_at) : new Date()
      const durationMs = endTime - startTime
      
      if (durationMs > 0) {
        const totalSeconds = Math.round(durationMs / 1000) // Round instead of floor for better accuracy
        const hours = Math.floor(totalSeconds / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60
        
        if (hours > 0) {
          return `${hours}h ${minutes}m`
        } else if (minutes > 0) {
          return `${minutes}m ${seconds}s`
        } else {
          return `${seconds}s`
        }
      }
    }
    
    return 'N/A'
  }
  const [selectedFile, setSelectedFile] = useState(null)
  const [sourceLang, setSourceLang] = useState('auto')
  const [targetLangs, setTargetLangs] = useState([])
  const [selectedDomain, setSelectedDomain] = useState(DEFAULT_DOMAIN)
  const [contextText, setContextText] = useState(() => t(DEFAULT_CONTEXT_KEY))
  const [outputFormat, setOutputFormat] = useState('auto')
  const [outputStructure, setOutputStructure] = useState('auto')
  const [uploadError, setUploadError] = useState('')
  const [quotaWarning, setQuotaWarning] = useState('')
  const [autoDetectionMessage, setAutoDetectionMessage] = useState('')
  const [showHistory, setShowHistory] = useState(false)
  const [currentHistoryPage, setCurrentHistoryPage] = useState(0)
  const [itemsPerPage] = useState(10) // Configurable items per page
  const [showImportModal, setShowImportModal] = useState(false)
  const [selectedJobForImport, setSelectedJobForImport] = useState(null)

  const queryClient = useQueryClient()

  // Fetch quota status for validation
  const { data: quotaStatus, error: quotaError } = useQuery({
    queryKey: ['quota-status'],
    queryFn: async () => {
      try {
        const response = await api.get('/user/quota/status')
        return response.data
      } catch (error) {
        console.warn('Failed to load quota status:', error)
        // Return a safe default that doesn't block functionality completely
        return {
          current_usage: 0,
          monthly_limit: 10000,
          percentage_used: 0,
          is_exceeded: false,
          can_start_jobs: true,
          warning_level: 'normal',
          error: 'Failed to load quota status'
        }
      }
    },
    refetchInterval: 60000, // Check every minute
    retry: 3, // Retry failed requests
  })

  // Fetch active jobs only (for polling)
  const { data: activeJobs = [], isLoading: activeJobsLoading } = useQuery({
    queryKey: ['active-jobs'],
    queryFn: async () => {
      console.log('Fetching active jobs from /user/jobs/active')
      const response = await api.get('/user/jobs/active')
      console.log('Active jobs response:', response.data)
      return response.data
    },
    refetchInterval: (data) => {
      // Only poll if there are active jobs, otherwise check every 30 seconds
      const hasActiveJobs = data && data.length > 0
      console.log('Active jobs polling interval:', hasActiveJobs ? '3s' : '30s', 'jobs:', data?.length || 0)
      return hasActiveJobs ? 3000 : 30000
    },
    refetchIntervalInBackground: false, // Don't poll when tab is not active
    staleTime: 2000, // Consider data stale after 2 seconds
  })

  // React Query-based history loading with persistent caching
  const { data: historyPageData, isLoading: historyLoading, refetch: refetchCurrentPage } = useQuery({
    queryKey: ['completed-jobs-page', currentHistoryPage, itemsPerPage],
    queryFn: async () => {
      console.log(`Loading history page ${currentHistoryPage} (items ${currentHistoryPage * itemsPerPage} - ${(currentHistoryPage + 1) * itemsPerPage})`)
      
      // Fetch all non-active jobs using enhanced API endpoint
      const response = await api.get('/user/jobs/enhanced', {
        params: {
          skip: currentHistoryPage * itemsPerPage * 2, // Request more to account for filtering
          limit: itemsPerPage * 2, // Request more items since we'll filter
          active_only: false // Get all jobs, not just active ones
        }
      })
      
      const allJobs = response.data.jobs || []
      
      // Filter for completed/finished jobs only
      const completedJobs = allJobs.filter(job => 
        job.status === 'completed' || job.status === 'finished'
      )
      
      // Take only the requested page size
      const enhancedJobs = completedJobs.slice(0, itemsPerPage)
      
      console.log('Loaded history page via React Query:', {
        page: currentHistoryPage,
        jobsLoaded: enhancedJobs.length,
        hasMore: enhancedJobs.length >= itemsPerPage
      })
      
      return {
        jobs: enhancedJobs,
        page: currentHistoryPage,
        hasMore: enhancedJobs.length >= itemsPerPage,
        totalJobsOnPage: enhancedJobs.length
      }
    },
    enabled: showHistory, // Only fetch when history is visible
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    cacheTime: 30 * 60 * 1000, // Keep in cache for 30 minutes after component unmount
  })

  // Get pagination metadata for all pages
  const { data: paginationData } = useQuery({
    queryKey: ['completed-jobs-pagination', itemsPerPage],
    queryFn: async () => {
      // Get a rough estimate by fetching more jobs and calculating
      const response = await api.get('/user/jobs/enhanced', {
        params: {
          skip: 0,
          limit: itemsPerPage * 5, // Get several pages worth to estimate total
          active_only: false
        }
      })
      
      const allJobs = response.data.jobs || []
      const completedJobs = allJobs.filter(job => 
        job.status === 'completed' || job.status === 'finished'
      )
      
      // Estimate total pages based on completion rate and pagination info
      const pagination = response.data.pagination || {}
      const completionRate = allJobs.length > 0 ? completedJobs.length / allJobs.length : 0
      const estimatedCompletedJobs = pagination.total ? Math.ceil(pagination.total * completionRate) : completedJobs.length
      const estimatedTotalPages = Math.ceil(estimatedCompletedJobs / itemsPerPage)
      
      console.log('Pagination metadata:', {
        estimatedCompletedJobs,
        estimatedTotalPages,
        completionRate: Math.round(completionRate * 100) + '%'
      })
      
      return {
        totalPages: estimatedTotalPages,
        totalJobs: estimatedCompletedJobs,
        hasMore: completedJobs.length >= itemsPerPage
      }
    },
    enabled: showHistory,
    staleTime: 10 * 60 * 1000, // Cache pagination info for 10 minutes
    cacheTime: 60 * 60 * 1000, // Keep in cache for 1 hour
  })

  // Function to load a specific page
  const loadHistoryPage = (page) => {
    console.log(`Navigating to history page ${page}`)
    setCurrentHistoryPage(page)
  }

  // Refresh entire history cache
  const refreshHistory = async () => {
    console.log('Refreshing entire history cache')
    // Invalidate all completed jobs queries to force refresh
    await Promise.all([
      queryClient.invalidateQueries({ 
        queryKey: ['completed-jobs-page'],
        exact: false // This will invalidate all pages
      }),
      queryClient.invalidateQueries({ 
        queryKey: ['completed-jobs-pagination']
      })
    ])
    // Reset to first page
    setCurrentHistoryPage(0)
  }


  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async ({ file, source_lang, target_langs, domain, context, output_format, output_structure }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('source_lang', source_lang)
      formData.append('domain', domain)
      formData.append('context', context)
      if (output_format) formData.append('output_format', output_format)
      if (output_structure) formData.append('output_structure', output_structure)
      target_langs.forEach(lang => formData.append('target_langs', lang))
      
      const response = await api.post('/user/jobs/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data
    },
    onSuccess: (data) => {
      console.log('Upload successful, job ID:', data.job_id)
      
      // Handle auto-detection info
      if (data.auto_detected_language && typeof data.auto_detected_language === 'object') {
        console.log('Language auto-detected:', data.auto_detected_language)
        const detectedInfo = data.auto_detected_language
        
        // Create appropriate message based on detection method
        let sourceMsg = ''
        if (detectedInfo.detected_from === 'xliff_content') {
          sourceMsg = `Source language auto-detected from XLIFF file content: ${detectedInfo.detected_lang}`
        } else if (detectedInfo.detected_from === 'filename') {
          sourceMsg = `Source language auto-detected from JSON filename: ${detectedInfo.detected_lang}`
        } else if (detectedInfo.detected_from === 'csv_filename') {
          sourceMsg = `Source language auto-detected from CSV filename: ${detectedInfo.detected_lang}`
        } else if (detectedInfo.detected_from === 'csv_column_header') {
          sourceMsg = `Source language auto-detected from CSV column header '${detectedInfo.source_column}': ${detectedInfo.detected_lang}`
        } else {
          sourceMsg = `Source language auto-detected from ${detectedInfo.detected_from || 'file'}: ${detectedInfo.detected_lang}`
        }
        
        setAutoDetectionMessage(sourceMsg)
        // Clear message after 8 seconds
        setTimeout(() => setAutoDetectionMessage(''), 8000)
      }
      
      queryClient.invalidateQueries({ queryKey: ['active-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['quota-status'] })
      setSelectedFile(null)
      setTargetLangs([])
      setSelectedDomain(DEFAULT_DOMAIN)
      setContextText(t(DEFAULT_CONTEXT_KEY))
      setUploadError('')
      setQuotaWarning('')
      setAutoDetectionMessage('')
    },
    onError: (error) => {
      console.error('Upload error:', error)
      const errorData = error.response?.data
      
      // Handle quota exceeded errors specifically
      if (error.response?.status === 429 && errorData?.detail?.error === 'quota_exceeded') {
        const quotaInfo = errorData.detail.quota_status
        setUploadError(`${t('quota.exceeded.uploadBlocked')}: ${errorData.detail.message}`)
        setQuotaWarning(t('quota.exceeded.uploadMessage', {
          usage: quotaInfo?.current_usage?.toLocaleString() || 'N/A',
          limit: quotaInfo?.monthly_limit?.toLocaleString() || 'N/A',
          days: quotaInfo?.days_until_renewal || 'N/A'
        }))
      } else {
        setUploadError(errorData?.detail?.message || errorData?.detail || error.message || 'Upload failed')
        setQuotaWarning('')
      }
    },
  })


  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setUploadError('')
    
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0]
      if (rejection.errors.some(e => e.code === 'file-too-large')) {
        setUploadError(t('error.fileTooLarge', { size: MAX_FILE_SIZE / 1024 / 1024 }))
      } else if (rejection.errors.some(e => e.code === 'file-invalid-type')) {
        setUploadError(t('error.invalidFileType'))
      }
      return
    }

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]
      setSelectedFile(file)
      
      // Language detection is now handled by the backend for all file types
      // Reset source language to auto when new file is selected
      setSourceLang('auto')
      setAutoDetectionMessage('')
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/json': ['.json'],
      'application/xml': ['.xlf', '.xliff'],
      'text/xml': ['.xlf', '.xliff'],
    },
    maxSize: MAX_FILE_SIZE,
    multiple: false,
  })

  const handleTargetLangChange = (langCode) => {
    setTargetLangs(prev => 
      prev.includes(langCode) 
        ? prev.filter(l => l !== langCode)
        : [...prev, langCode]
    )
  }

  const handleSubmit = () => {
    // Clear previous errors
    setUploadError('')
    setQuotaWarning('')

    if (!selectedFile || targetLangs.length === 0) {
      setUploadError(t('error.selectFileAndLanguage'))
      return
    }

    // Pre-upload quota validation
    if (quotaStatus?.is_exceeded) {
      setUploadError(t('quota.exceeded.cannotStart'))
      setQuotaWarning(t('quota.exceeded.renewsIn', { 
        days: quotaStatus.days_until_renewal || 'N/A' 
      }))
      return
    }

    // Double-check quota status is loaded
    if (!quotaStatus) {
      setUploadError('Please wait for quota status to load before uploading.')
      return
    }

    // Warning for users near quota limit
    if (quotaStatus?.warning_level === 'warning' || quotaStatus?.warning_level === 'critical') {
      setQuotaWarning(t('quota.warning.beforeUpload', {
        percentage: quotaStatus.percentage_used || 0,
        remaining: quotaStatus.remaining?.toLocaleString() || 'N/A'
      }))
    }

    // Proceed with upload
    uploadMutation.mutate({
      file: selectedFile,
      source_lang: sourceLang,
      target_langs: targetLangs,
      domain: selectedDomain,
      context: contextText,
      output_format: outputFormat,
      output_structure: outputStructure,
    })
  }

  // TMS Import handlers
  const handleImportToTMS = (job) => {
    // Navigate to TMS integration page with preselected job
    navigate(`/tms-integration?job=${job.id}`)
  }

  const handleImportSuccess = (data) => {
    // Optionally refresh job list or show success message
    console.log('Import successful:', data)
    // Could add a toast notification here
  }

  const handleCloseImportModal = () => {
    setShowImportModal(false)
    setSelectedJobForImport(null)
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'finished':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />
      case 'processing':
        return <Clock className="w-5 h-5 text-blue-500 animate-spin" />
      default:
        return <Clock className="w-5 h-5 text-yellow-500" />
    }
  }

  const availableTargetLangs = SUPPORTED_LANGUAGES.filter(lang => lang.code !== sourceLang)

  return (
    <div className="page-transition space-y-6">
      
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('translation.center')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('translation.uploadFiles')}
        </p>
      </div>

      {/* Quota Status */}
      <QuotaStatus showDetails={false} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Section */}
        <Card>
          <Card.Header>
            <Card.Title>{t('translation.newTranslation')}</Card.Title>
          </Card.Header>
          <Card.Content className="space-y-6">
            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('translation.uploadFile')}
              </label>
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-primary-400'
                }`}
              >
                <input {...getInputProps()} />
                {selectedFile ? (
                  <div className="flex items-center justify-center space-x-2">
                    <File className="w-8 h-8 text-primary-600" />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {selectedFile.name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedFile(null)
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      {t('translation.dropFileHere')}
                    </p>
                    <p className="text-sm text-gray-500">
                      {t('translation.supportedFiles')}
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Source Language */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('translation.sourceLanguage')}
              </label>
              <select
                value={sourceLang}
                onChange={(e) => setSourceLang(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
              >
                <option value="auto">🤖 {t('translation.autoDetect', 'Auto-detect from file')}</option>
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {t(lang.nameKey)}
                  </option>
                ))}
              </select>
            </div>

            {/* Target Languages */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('translation.targetLanguages')}
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {availableTargetLangs.map((lang) => (
                  <label
                    key={lang.code}
                    className="flex items-center space-x-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={targetLangs.includes(lang.code)}
                      onChange={() => handleTargetLangChange(lang.code)}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-900 dark:text-white">
                      {lang.flag} {t(lang.nameKey)}
                    </span>
                  </label>
                ))}
              </div>
              {targetLangs.length > 0 && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  {t('translation.selectedLanguages', { count: targetLangs.length })}
                </p>
              )}
            </div>

            {/* Domain Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('translation.contentDomain')}
              </label>
              <select
                value={selectedDomain}
                onChange={(e) => setSelectedDomain(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
              >
                {SUPPORTED_DOMAINS.map((domain) => (
                  <option key={domain.code} value={domain.code}>
                    {domain.icon} {t(`domains.${domain.code}.name`)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t(`domains.${selectedDomain}.description`)}
              </p>
            </div>

            {/* Output Format Selection */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('translation.outputFormat')}
                </label>
                <select
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                >
                  <option value="auto">{t('translation.outputFormatOptions.auto')}</option>
                  <option value="json">{t('translation.outputFormatOptions.json')}</option>
                  <option value="csv">{t('translation.outputFormatOptions.csv')}</option>
                  <option value="excel">{t('translation.outputFormatOptions.excel')}</option>
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('translation.outputFormatDescription')}
                </p>
              </div>

              {(outputFormat === 'json' || (outputFormat === 'auto' && selectedFile?.name?.endsWith('.json'))) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('translation.outputStructure')}
                  </label>
                  <select
                    value={outputStructure}
                    onChange={(e) => setOutputStructure(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                  >
                    <option value="auto">{t('translation.outputStructureOptions.auto')}</option>
                    <option value="per-language">{t('translation.outputStructureOptions.perLanguage')}</option>
                    <option value="multi-language">{t('translation.outputStructureOptions.multiLanguage')}</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {outputStructure === 'per-language' 
                      ? t('translation.perLanguageDescription')
                      : outputStructure === 'multi-language'
                      ? t('translation.multiLanguageDescription')
                      : t('translation.autoStructureDescription')
                    }
                  </p>
                </div>
              )}
            </div>

            {/* Context Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('translation.translationContext')}
              </label>
              <textarea
                value={contextText}
                onChange={(e) => {
                  const value = e.target.value
                  if (value.length <= MAX_CONTEXT_LENGTH) {
                    setContextText(value)
                  }
                }}
                placeholder={t('translation.contextPlaceholder')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white resize-none"
              />
              <div className="flex justify-between items-center mt-1">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('translation.contextDescription')}
                </p>
                <span className={`text-xs ${
                  contextText.length > MAX_CONTEXT_LENGTH * 0.9 
                    ? 'text-orange-500' 
                    : contextText.length > MAX_CONTEXT_LENGTH * 0.8 
                    ? 'text-yellow-500' 
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {contextText.length}/{MAX_CONTEXT_LENGTH}
                </span>
              </div>
            </div>

            {/* Error Message */}
            {uploadError && (
              <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <p className="text-sm text-red-600 dark:text-red-400">{uploadError}</p>
                </div>
              </div>
            )}

            {/* Quota Warning Message */}
            {quotaWarning && (
              <div className="bg-orange-50 dark:bg-orange-900/50 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-orange-500" />
                  <p className="text-sm text-orange-600 dark:text-orange-400">{quotaWarning}</p>
                </div>
              </div>
            )}

            {/* Auto-Detection Success Message */}
            {autoDetectionMessage && (
              <div className="bg-green-50 dark:bg-green-900/50 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <p className="text-sm text-green-600 dark:text-green-400">{autoDetectionMessage}</p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <Button
              data-testid="start-translation-button"
              onClick={handleSubmit}
              className="w-full"
              loading={uploadMutation.isPending}
              disabled={
                !selectedFile || 
                targetLangs.length === 0 || 
                uploadMutation.isPending || 
                quotaStatus?.is_exceeded ||
                !quotaStatus  // Disable until quota status loads
              }
            >
              <Play className="w-4 h-4 mr-2" />
              {quotaStatus?.is_exceeded 
                ? t('quota.exceeded.uploadBlocked') 
                : !quotaStatus
                ? 'Loading quota status...'
                : t('translation.startTranslation')
              }
            </Button>
          </Card.Content>
        </Card>

        {/* Jobs List */}
        <Card>
          <Card.Header className="flex items-center justify-between">
            <Card.Title>{t('translation.translationJobs')}</Card.Title>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: ['active-jobs'] })
                // Only refresh history if user has explicitly loaded it and it's currently shown  
                if (showHistory) {
                  console.log('Refreshing history on user refresh request')
                  refreshHistory()
                }
              }}
              disabled={activeJobsLoading || historyLoading}
            >
              <RefreshCw className={`w-4 h-4 ${(activeJobsLoading || historyLoading) ? 'animate-spin' : ''}`} />
            </Button>
          </Card.Header>
          <Card.Content>
            {/* Active Jobs Section */}
            {activeJobsLoading ? (
              <div className="space-y-4">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : activeJobs.length > 0 ? (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('translation.activeJobs')} ({activeJobs.length})
                </h3>
                {activeJobs.map((job) => (
                  <div key={job.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        {getStatusIcon(job.status)}
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {job.filename || 'Untitled'}
                          </p>
                          <div data-testid="target-languages" className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                            <Languages className="w-4 h-4" />
                            <span>{job.source_lang} → {job.target_langs?.join(', ')}</span>
                          </div>
                        </div>
                      </div>
                      <span data-testid="job-status" className={`px-2 py-1 rounded-full text-xs font-medium ${JOB_STATUS_COLORS[job.status]}`}>
                        {t(JOB_STATUS_LABELS[job.status])}
                      </span>
                    </div>

                    {job.progress && job.status === 'processing' && (
                      <div className="mb-3">
                        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                          <span>{t('translation.progress')}</span>
                          <span>{job.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${job.progress}%` }}
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex items-center space-x-2">
                      {job.status === 'failed' && job.error && (
                        <p className="text-sm text-red-600 dark:text-red-400">
                          {t('translation.error')}: {job.error}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {/* History Toggle Button */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                data-testid="load-completed-jobs-button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!showHistory) {
                    // Show history - React Query will handle caching automatically
                    console.log('Showing history - React Query will load from cache if available')
                    setCurrentHistoryPage(0)
                    setShowHistory(true)
                  } else {
                    // Hide history but preserve cache in React Query
                    console.log('Hiding history - preserving React Query cache')
                    setShowHistory(false)
                    setCurrentHistoryPage(0)
                  }
                }}
                loading={historyLoading && !showHistory}
              >
                {showHistory ? t('translation.hideCompletedJobs') : t('translation.loadCompletedJobs')}
              </Button>
            </div>

            {/* Enhanced History Section */}
            {showHistory && (
              <div className="mt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('translation.completedJobs')} 
                    {paginationData?.totalJobs > 0 && (
                      <span className="ml-2 text-xs text-gray-500">
                        (~{paginationData.totalJobs} total)
                      </span>
                    )}
                  </h3>
                  <span className="text-xs text-gray-500">
                    {historyPageData ? t('translation.cached') : t('translation.loading')}
                  </span>
                </div>
                
                {/* Pagination Controls */}
                {paginationData && paginationData.totalPages > 1 && (
                  <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                    <span>
                      {t('translation.page')} {currentHistoryPage + 1} {t('translation.of')} {paginationData.totalPages}
                    </span>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => loadHistoryPage(currentHistoryPage - 1)}
                        disabled={currentHistoryPage === 0 || historyLoading}
                      >
                        {t('translation.previous')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => loadHistoryPage(currentHistoryPage + 1)}
                        disabled={currentHistoryPage >= paginationData.totalPages - 1 || historyLoading}
                      >
                        {t('translation.next')}
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Current page jobs */}
                {(() => {
                  const currentPageJobs = historyPageData?.jobs || []
                  
                  if (currentPageJobs.length > 0) {
                    return (
                      <div className="space-y-4">
                        {currentPageJobs.map((job) => (
                          <div key={job.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center space-x-3">
                                {getStatusIcon(job.status)}
                                <div>
                                  <p className="font-medium text-gray-900 dark:text-white">
                                    {job.filename || 'Untitled'}
                                  </p>
                                  <div data-testid="target-languages" className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                                    <Languages className="w-4 h-4" />
                                    <span>{job.source_lang} → {job.target_langs?.join(', ')}</span>
                                  </div>
                                </div>
                              </div>
                              <span data-testid="job-status" className={`px-2 py-1 rounded-full text-xs font-medium ${JOB_STATUS_COLORS[job.status]}`}>
                                {t(JOB_STATUS_LABELS[job.status])}
                              </span>
                            </div>

                            {/* Enhanced Metadata */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-xs text-gray-600 dark:text-gray-400">
                              <div className="flex items-center space-x-1">
                                <Calendar className="w-3 h-3" />
                                <span>{t('translation.started')}: {job.created_display || new Date(job.created_at).toLocaleString()}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Timer className="w-3 h-3" />
                                <span>{t('translation.duration')}: {calculateDuration(job)}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Users className="w-3 h-3" />
                                <span>{t('translation.languages')}: {job.target_lang_count || (Array.isArray(job.target_langs) ? job.target_langs.length : 1)}</span>
                              </div>
                              {(() => {
                                const characters = job.total_characters_used || job.llm_total_characters || 0;
                                const tokens = job.total_tokens_used || job.llm_total_tokens || 0;
                                
                                if (characters > 0) {
                                  return (
                                    <div className="flex items-center space-x-1">
                                      <Hash className="w-3 h-3" />
                                      <span>{t('translation.characters')}: {characters.toLocaleString()}</span>
                                    </div>
                                  );
                                } else if (tokens > 0) {
                                  return (
                                    <div className="flex items-center space-x-1">
                                      <Hash className="w-3 h-3" />
                                      <span>{t('translation.tokens')}: {tokens.toLocaleString()}</span>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>

                            <div className="flex items-center space-x-2">
                              {(job.status === 'completed' || job.status === 'finished') && (
                                <>
                                  <JobDownloadButton job={job} />
                                  <Button
                                    data-testid="import-to-tms-button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleImportToTMS(job)}
                                    className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                  >
                                    <Upload className="w-4 h-4 mr-1" />
                                    {t('tms.importToTMS')}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate(`/review/${job.id}`)}
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    {t('translation.review')}
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  } else if (historyLoading) {
                    return (
                      <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="animate-pulse border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
                            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-3" />
                            <div className="grid grid-cols-2 gap-2">
                              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded" />
                              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  } else {
                    return (
                      <div className="text-center py-8">
                        <Languages className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 dark:text-gray-400">
                          {t('translation.noCompletedJobs')}
                        </p>
                      </div>
                    )
                  }
                })()}
              </div>
            )}

            {/* Empty State - only show if no active jobs and history not shown */}
            {activeJobs.length === 0 && !showHistory && !activeJobsLoading && (
              <div className="text-center py-8">
                <Languages className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  {t('translation.noJobsYet')}
                </p>
              </div>
            )}
          </Card.Content>
        </Card>
      </div>

      {/* TMS Import Modal */}
      {selectedJobForImport && (
        <ImportJobModal
          isOpen={showImportModal}
          onClose={handleCloseImportModal}
          onSuccess={handleImportSuccess}
          preSelectedJobId={selectedJobForImport.uuid || selectedJobForImport.id}
          preSelectedJobTitle={selectedJobForImport.filename || selectedJobForImport.original_filename}
        />
      )}
    </div>
  )
}

export default TranslationPage